import React, { useEffect, useState } from 'react';
import { Form, InputNumber, DatePicker, Button, message, Space, Typography } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { getConfig } from "../../../config";
import { SaveOutlined, UndoOutlined } from "@ant-design/icons";
import { useProjectContext } from '../../../context/ProjectContext';
import {useDirtyContext} from "../../../context/DirtyContext";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/de_DE";

const { Title } = Typography;
const { API_BASE_URL } = getConfig();

interface ProjectConfigurationProps {
    selectedProject?: string;
    setProjectConfigData?: (data: ProjectData) => void;
}

const ProjectConfiguration: React.FC<ProjectConfigurationProps> = ({ selectedProject, setProjectConfigData }) => {
    const { selectedProject: contextProject } = useProjectContext();
    const [data, setData] = useState<ProjectData | null>(null);
    const [originalData, setOriginalData] = useState<ProjectData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isChanged, setIsChanged] = useState(false);
    const { isDirty, setDirty } = useDirtyContext();
    const activeProject = selectedProject || contextProject;

    useEffect(() => {
        if (activeProject) {
            fetchData(activeProject);
        }
    }, [activeProject]);

    useEffect(() => {
        if (data && setProjectConfigData) {
            setProjectConfigData(data);
        }
    }, [data, setProjectConfigData]);

    const fetchData = async (projectNo: string) => {
        setIsLoading(true);
        try {
            const response = await axios.get<ProjectData[]>(
                `${API_BASE_URL}/getNetworkPlanProjectData?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}`
            );
            const project = response.data[0];
            const formatted = {
                ...project,
                ordersReceiptionDate: moment(project.ordersReceiptionDate, ['DD.MM.YYYY', moment.ISO_8601]).format('DD.MM.YYYY'),
                kickOffDate: moment(project.kickOffDate, ['DD.MM.YYYY', moment.ISO_8601]).format('DD.MM.YYYY'),
            };

            setData(formatted);
            setOriginalData(formatted);
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error('Fehler beim Laden der Projektdaten.');
        } finally {
            setIsLoading(false);
        }
    };


    const handleFieldChange = (field: keyof ProjectData, value: any) => {
        setDirty(true);
        setData((prev) => {
            if (!prev) return null;
            const updatedData = { ...prev, [field]: value };
            setIsChanged(
                Object.keys(updatedData).some(
                    (key) => updatedData[key as keyof ProjectData] !== originalData?.[key as keyof ProjectData]
                )
            );
            return updatedData;
        });
    };

    const handleSave = async () => {
        if (!data) return;
        setIsLoading(true);
        setIsSaving(true);
        setDirty(false);
        try {
            const payload = {
                ...data,
                ordersReceiptionDate: data.ordersReceiptionDate,
                kickOffDate: data.kickOffDate,
            };
            await axios.post(`${API_BASE_URL}/saveChangedNetworkPlanProject`, payload);
            setOriginalData(data);
            setIsChanged(false);
            message.success('Projektdaten erfolgreich gespeichert.');
        } catch (error) {
            console.error('Error saving data:', error);
            message.error('Fehler beim Speichern der Projektdaten.');
        } finally {
            setIsLoading(false);
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        setData(originalData);
        setIsChanged(false);
        setDirty(false);
    };

    return (
        <div style={{ padding: '20px' }}>
            <Space
                style={{
                    marginBottom: 16,
                    width: '100%',
                    justifyContent: 'flex-end', // Aligns items to the right
                    display: 'flex', // Ensures the flexbox behavior applies
                }}
            >
                <Button
                    icon={<UndoOutlined />}
                    onClick={handleReset}
                    style={{ marginRight: 8 }}
                >
                    Daten neu laden
                </Button>
                {!selectedProject && (
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={isSaving}
                        disabled={!isChanged}
                        onClick={handleSave}
                    >
                        Speichern
                    </Button>
                )}
            </Space>

            <Form layout="vertical" style={{ width: '50%' }}>
                <Form.Item label="Auftragseingang" required>
                    <DatePicker
                        locale={locale}
                        value={data ? dayjs(data.ordersReceiptionDate, 'DD.MM.YYYY') : null}
                        format="DD.MM.YYYY"
                        disabled={true}
                        onChange={(date) => handleFieldChange('ordersReceiptionDate', date ? date.format('DD.MM.YYYY') : '')}
                        style={{
                            backgroundColor: data?.ordersReceiptionDate !== originalData?.ordersReceiptionDate ? '#d9f7be' : undefined,
                        }}
                    />
                </Form.Item>

                <Form.Item label="Startsitzung" required>
                    <DatePicker
                        locale={locale}
                        value={data?.kickOffDate ? dayjs(data.kickOffDate, 'DD.MM.YYYY') : null}
                        format="DD.MM.YYYY"
                        onChange={(date) => handleFieldChange('kickOffDate', date ? date.format('DD.MM.YYYY') : '')}
                        style={{
                            backgroundColor: data?.kickOffDate !== originalData?.kickOffDate ? '#d9f7be' : undefined,
                        }}
                    />
                </Form.Item>

                <Form.Item label="Freigabedauer Kunde (AT)" required>
                    <InputNumber
                        min={0}
                        value={data?.approvalDuration}
                        onChange={(value) => handleFieldChange('approvalDuration', value || 0)}
                        style={{
                            backgroundColor:
                                data?.approvalDuration !== originalData?.approvalDuration ? '#d9f7be' : undefined,
                            width: '100%',
                        }}
                    />
                </Form.Item>
            </Form>
        </div>
    );
};

export default ProjectConfiguration;